import React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Terms = () => {
  const lastUpdated = "March 25, 2025";
  
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>
        
        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle><strong>PLEASE READ CAREFULLY</strong></AlertTitle>
          By accessing or using AI This Job, you agree to be bound by these Terms of Service. If you do not agree to these terms, you must not access or use our services.
        </Alert>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          1. Acceptance of Terms
        </Typography>
        <Typography paragraph>
          By accessing or using AI This Job ("Service"), you agree to be bound by these Terms of Service ("Terms"). These Terms apply to all visitors, users, and others who access or use the Service. If you disagree with any part of the terms, you may not access the Service.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          2. Description of Service
        </Typography>
        <Typography paragraph>
          AI This Job is an AI-powered automation platform that utilizes Claude Computer Use to automate various tasks. The Service allows users to create, schedule, and run automated tasks using AI assistant technology.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          3. Disclaimer of Warranties and Limitation of Liability
        </Typography>
        <Typography paragraph>
          <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.</strong>
        </Typography>
        <Typography paragraph>
          <strong>AI This Job, its subsidiaries, affiliates, and licensors do not warrant that:</strong>
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="The Service will function uninterrupted, secure, or available at any particular time or location;" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Any errors or defects will be corrected;" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="The Service is free of viruses or other harmful components;" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="The results of using the Service will meet your requirements or expectations." />
          </ListItem>
        </List>
        
        <Box sx={{ mt: 2, mb: 4, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography sx={{ fontWeight: 'bold' }}>
            IN NO EVENT SHALL AI THIS JOB, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Your access to or use of or inability to access or use the Service;" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Any conduct or content of any third party on the Service;" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Any content obtained from the Service;" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Financial or business decisions made based on information provided by the Service;" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Unauthorized access, use, or alteration of your transmissions or content." />
            </ListItem>
          </List>
        </Box>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          4. Risk Acknowledgment for AI Automation
        </Typography>
        <Typography paragraph>
          AI This Job extends the functionality of Claude Computer Use, which is a beta feature with unique risks. By using our Service, you acknowledge and accept the following risks:
        </Typography>
        
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Automation Risks</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The Service uses AI to automate tasks, which may result in unexpected behavior, errors, or unintended consequences. You acknowledge that automated processes may not always perform as expected and could potentially impact your data or systems.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Important Safety Recommendation</AlertTitle>
              Use a dedicated virtual machine or container with minimal privileges to prevent direct system attacks or accidents when running automation tasks.
            </Alert>
          </AccordionDetails>
        </Accordion>
        
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Data Security Concerns</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The Service may process data that you provide. You should avoid providing sensitive information such as login credentials, financial information, or personal data that could lead to harm if misused or exposed.
            </Typography>
            <Alert severity="warning">
              Avoid giving the AI access to sensitive data, such as account login information, to prevent information theft.
            </Alert>
          </AccordionDetails>
        </Accordion>
        
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Internet Access Risks</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              When using features that interact with the internet, there are heightened risks related to exposure to potentially malicious content or unauthorized actions.
            </Typography>
            <Alert severity="warning">
              Consider limiting internet access to an allowlist of domains to reduce exposure to malicious content when using our automation services.
            </Alert>
          </AccordionDetails>
        </Accordion>
        
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 'medium' }}>Prompt Injection Vulnerabilities</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              In some circumstances, Claude might follow commands found in content even if it conflicts with the user's instructions. For example, instructions on webpages or contained in images may override user instructions or cause Claude to make mistakes.
            </Typography>
            <Alert severity="warning">
              Take precautions to isolate Claude from sensitive data and actions to avoid risks related to prompt injection.
            </Alert>
          </AccordionDetails>
        </Accordion>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          5. Human Oversight Requirement
        </Typography>
        <Typography paragraph>
          You agree to maintain human oversight of all automation processes initiated through our Service. Specifically:
        </Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Required Human Oversight</AlertTitle>
          Ask a human to confirm decisions that may result in meaningful real-world consequences as well as any tasks requiring affirmative consent, such as accepting cookies, executing financial transactions, or agreeing to terms of service.
        </Alert>
        <Typography paragraph>
          Users are solely responsible for all consequences arising from automated actions performed by the Service, including financial transactions, data modifications, or communications sent.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          6. Prohibited Uses
        </Typography>
        <Typography paragraph>
          You are expressly prohibited from using the Service for any illegal, harmful, or unethical purposes, including but not limited to:
        </Typography>
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Any activity that violates applicable laws or regulations" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Unauthorized access to systems, data, or networks" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Creation or distribution of malicious software" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Harassment, abuse, or harm of individuals or groups" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Generation or distribution of illegal content" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Fraudulent or deceptive practices" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <GavelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Activities that could damage the reputation or functionality of AI This Job" />
            </ListItem>
          </List>
        </Box>
        <Typography paragraph sx={{ fontWeight: 'medium' }}>
          AI This Job reserves the right to terminate your access to the Service immediately, without prior notice, if we determine, in our sole discretion, that you have violated these prohibited uses. In cases of illegal activity, we will cooperate with relevant law enforcement authorities.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          7. Indemnification
        </Typography>
        <Typography paragraph>
          You agree to defend, indemnify, and hold harmless AI This Job, its parent company, officers, directors, employees, and agents, from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          8. Data Usage and Privacy
        </Typography>
        <Typography paragraph>
          When you use our Service, you grant us a limited, non-exclusive license to use your data solely for the purpose of providing and improving our Service. <strong>You retain ownership of all data you enter on our site and all content you create using our Service.</strong>
        </Typography>
        <Typography paragraph>
          To provide our Service, we need limited permissions to:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Process your inputs and instructions to perform the requested automations" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Store your tasks, automations, and workflows on our servers" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Execute your automations as scheduled or requested" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Monitor performance and analyze usage patterns to improve our Service" />
          </ListItem>
        </List>
        <Typography paragraph>
          We respect your privacy and will handle all your data in accordance with our Privacy Policy. We will never sell your personal data to any third party. Please refer to our Privacy Policy for more details on how we handle your information.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          9. Termination
        </Typography>
        <Typography paragraph>
          We may terminate or suspend access to our Service immediately, without prior notice or liability, for any reason whatsoever, including, without limitation, if you breach the Terms.
        </Typography>
        <Typography paragraph>
          All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          10. Changes to Terms
        </Typography>
        <Typography paragraph>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
        </Typography>
        
        <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'medium' }}>
          11. Governing Law
        </Typography>
        <Typography paragraph>
          These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
        </Typography>
        <Typography paragraph>
          Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
        </Typography>
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ mt: 4, p: 3, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            By using AI This Job, you acknowledge that:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText primary="You have read and understood these Terms of Service" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText primary="You accept all risks associated with using AI automation technology" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText primary="You will maintain appropriate human oversight for all automation tasks" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText primary="You will not use the Service for any prohibited or illegal purposes" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon sx={{ color: 'primary.contrastText' }} />
              </ListItemIcon>
              <ListItemText primary="You release AI This Job from liability for any adverse effects resulting from your use of the Service" />
            </ListItem>
          </List>
        </Box>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            If you have any questions about these Terms, please contact us at legal@aithisjob.com
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Terms;